import { Controller } from "@hotwired/stimulus"
import { Turbo } from "@hotwired/turbo-rails"

/**
 * Pull-to-refresh for installed / standalone PWAs.
 *
 * iOS home-screen web apps have no Safari chrome, so the usual pull-to-refresh
 * gesture is missing. This controller restores it with a Turbo page reload.
 *
 * Activates only when:
 * - display-mode is standalone (or iOS navigator.standalone), and
 * - the page is not running inside Hotwire Native (native has its own control)
 *
 * Usage:
 *   <body data-controller="pull-to-refresh">
 *
 * @example Manual refresh from another controller
 *   this.application.getControllerForElementAndIdentifier(
 *     document.body,
 *     "pull-to-refresh"
 *   )?.refresh()
 */
export default class extends Controller {
  static values = {
    threshold: { type: Number, default: 72 },
    maxPull: { type: Number, default: 120 }
  }

  /**
   * Sets up touch listeners and the pull indicator when running as a PWA.
   *
   * @returns {void}
   */
  connect() {
    this.enabled = this.isStandalonePwa() && !this.isHotwireNative()
    if (!this.enabled) return

    this.pulling = false
    this.refreshing = false
    this.startY = 0
    this.currentY = 0

    this.boundTouchStart = this.onTouchStart.bind(this)
    this.boundTouchMove = this.onTouchMove.bind(this)
    this.boundTouchEnd = this.onTouchEnd.bind(this)
    this.boundTurboLoad = this.onTurboLoad.bind(this)

    document.addEventListener("touchstart", this.boundTouchStart, { passive: true })
    document.addEventListener("touchmove", this.boundTouchMove, { passive: false })
    document.addEventListener("touchend", this.boundTouchEnd, { passive: true })
    document.addEventListener("turbo:load", this.boundTurboLoad)

    this.ensureIndicator()
  }

  /**
   * Removes listeners and the indicator element.
   *
   * @returns {void}
   */
  disconnect() {
    if (!this.enabled) return

    document.removeEventListener("touchstart", this.boundTouchStart)
    document.removeEventListener("touchmove", this.boundTouchMove)
    document.removeEventListener("touchend", this.boundTouchEnd)
    document.removeEventListener("turbo:load", this.boundTurboLoad)

    this.indicator?.remove()
    this.indicator = null
  }

  /**
   * Reloads the current page via Turbo (replace, no history entry).
   *
   * @returns {void}
   */
  refresh() {
    if (this.refreshing) return

    this.refreshing = true
    this.setIndicatorState("refreshing")
    Turbo.visit(window.location.href, { action: "replace" })
  }

  /**
   * @returns {boolean}
   * @private
   */
  isStandalonePwa() {
    return (
      window.matchMedia("(display-mode: standalone)").matches ||
      window.navigator.standalone === true
    )
  }

  /**
   * @returns {boolean}
   * @private
   */
  isHotwireNative() {
    return Boolean(document.querySelector('meta[name="hotwire-native-app"]'))
  }

  /**
   * @param {TouchEvent} event
   * @private
   */
  onTouchStart(event) {
    if (this.refreshing || !this.isAtTop()) return

    this.pulling = true
    this.startY = event.touches[0].clientY
    this.currentY = this.startY
  }

  /**
   * @param {TouchEvent} event
   * @private
   */
  onTouchMove(event) {
    if (!this.pulling || this.refreshing) return

    this.currentY = event.touches[0].clientY
    const distance = this.pullDistance()

    if (distance <= 0 || !this.isAtTop()) {
      this.resetIndicator()
      return
    }

    // Prevent the page from rubber-banding while we own the gesture
    if (distance > 8) {
      event.preventDefault()
    }

    this.updateIndicator(distance)
  }

  /**
   * @private
   */
  onTouchEnd() {
    if (!this.pulling || this.refreshing) return

    const distance = this.pullDistance()
    this.pulling = false

    if (distance >= this.thresholdValue) {
      this.refresh()
    } else {
      this.resetIndicator()
    }
  }

  /**
   * Ends the refreshing state after Turbo finishes the visit.
   *
   * @private
   */
  onTurboLoad() {
    if (!this.refreshing) return

    this.refreshing = false
    this.resetIndicator()
  }

  /**
   * @returns {boolean}
   * @private
   */
  isAtTop() {
    return (window.scrollY || document.documentElement.scrollTop || 0) <= 0
  }

  /**
   * @returns {number}
   * @private
   */
  pullDistance() {
    return Math.min(
      Math.max(0, this.currentY - this.startY),
      this.maxPullValue
    )
  }

  /**
   * Creates the floating pull indicator once.
   *
   * @private
   */
  ensureIndicator() {
    if (this.indicator) return

    const el = document.createElement("div")
    el.setAttribute("data-pull-to-refresh-indicator", "")
    el.setAttribute("aria-hidden", "true")
    el.className = [
      "pointer-events-none fixed left-1/2 z-[70] -translate-x-1/2",
      "flex h-10 w-10 items-center justify-center rounded-full",
      "bg-white dark:bg-surface-800 shadow-lg border border-surface-200 dark:border-surface-700",
      "opacity-0 transition-opacity duration-150"
    ].join(" ")

    el.innerHTML = `
      <svg data-ptr-icon class="h-5 w-5 text-surface-600 dark:text-surface-300 transition-transform duration-150" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
      </svg>
    `

    document.body.appendChild(el)
    this.indicator = el
    this.icon = el.querySelector("[data-ptr-icon]")
  }

  /**
   * @param {number} distance
   * @private
   */
  updateIndicator(distance) {
    this.ensureIndicator()

    const progress = Math.min(distance / this.thresholdValue, 1)
    const translateY = Math.min(distance * 0.45, 56)

    this.indicator.style.opacity = String(Math.min(progress * 1.2, 1))
    this.indicator.style.transform = `translate(-50%, ${translateY}px)`
    this.icon.style.transform = `rotate(${progress * 180}deg)`

    if (progress >= 1) {
      this.setIndicatorState("ready")
    } else {
      this.setIndicatorState("pulling")
    }
  }

  /**
   * @param {"pulling"|"ready"|"refreshing"} state
   * @private
   */
  setIndicatorState(state) {
    this.ensureIndicator()

    if (state === "refreshing") {
      this.indicator.style.opacity = "1"
      this.indicator.style.transform = "translate(-50%, 48px)"
      this.icon.classList.add("animate-spin")
      this.icon.style.transform = ""
    } else {
      this.icon.classList.remove("animate-spin")
    }
  }

  /**
   * Hides and resets the indicator.
   *
   * @private
   */
  resetIndicator() {
    if (!this.indicator) return

    this.icon.classList.remove("animate-spin")
    this.icon.style.transform = ""
    this.indicator.style.opacity = "0"
    this.indicator.style.transform = "translate(-50%, 0)"
  }
}
